/**
 * This browser's device key pair (X25519). The private key is a non-extractable WebCrypto key
 * stored in IndexedDB; only the ECDH step runs in WebCrypto, everything else is in the Rust core.
 */
import {
  generateSoftwareDeviceKey,
  unwrapDeviceWithSecret,
  unwrapDeviceWithShared,
  wrapDevice,
} from "@envstash/core-wasm";
import type { WrappedVaultKey } from "@envstash/protocol";
import { b64Decode, b64Encode, toArrayBuffer } from "./bytes.js";
import { db, type DeviceRecord } from "./db.js";

export const PLACEHOLDER_DEVICE_ID = "00000000-0000-4000-8000-000000000000";

async function webCryptoX25519Available(): Promise<boolean> {
  try {
    const k = await crypto.subtle.generateKey({ name: "X25519" }, false, ["deriveBits"]);
    return typeof k === "object" && "privateKey" in k;
  } catch {
    return false;
  }
}

export async function getOrCreateDeviceKey(): Promise<DeviceRecord> {
  const d = await db();
  const existing = await d.get("device", "current");
  if (existing) return existing;
  let record: DeviceRecord;
  if (await webCryptoX25519Available()) {
    const pair = (await crypto.subtle.generateKey({ name: "X25519" }, false, ["deriveBits"])) as CryptoKeyPair;
    const raw = new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey));
    record = {
      id: "current",
      deviceId: null,
      publicKey: raw,
      cryptoKey: pair.privateKey,
      softwareSecret: null,
      createdAt: Date.now(),
    };
  } else {
    const sw = generateSoftwareDeviceKey();
    record = {
      id: "current",
      deviceId: null,
      publicKey: sw.public,
      cryptoKey: null,
      softwareSecret: sw.secret,
      createdAt: Date.now(),
    };
  }
  await d.put("device", record);
  return record;
}

export async function rememberDeviceId(deviceId: string): Promise<void> {
  const d = await db();
  const rec = await d.get("device", "current");
  if (rec && rec.deviceId !== deviceId) await d.put("device", { ...rec, deviceId });
}

export function devicePublicKeyB64(rec: DeviceRecord): string {
  return b64Encode(rec.publicKey);
}

/** Wrap the vault key to this device (the server rewrites the placeholder id when needed). */
export function wrapForThisDevice(vk: Uint8Array, rec: DeviceRecord, deviceId: string | null): WrappedVaultKey {
  return wrapDevice(vk, deviceId ?? PLACEHOLDER_DEVICE_ID, devicePublicKeyB64(rec));
}

/** Open a device wrap addressed to this browser. */
export async function unwrapWithThisDevice(wrap: WrappedVaultKey, rec: DeviceRecord): Promise<Uint8Array> {
  if (wrap.kind !== "device") throw new Error("not a device wrap");
  if (rec.cryptoKey) {
    const ephPk = await crypto.subtle.importKey(
      "raw",
      toArrayBuffer(b64Decode(wrap.eph_pk)),
      { name: "X25519" },
      false,
      [],
    );
    const shared = new Uint8Array(
      await crypto.subtle.deriveBits({ name: "X25519", public: ephPk }, rec.cryptoKey, 256),
    );
    return unwrapDeviceWithShared(wrap, shared, rec.publicKey);
  }
  if (rec.softwareSecret) return unwrapDeviceWithSecret(wrap, rec.softwareSecret);
  throw new Error("device key unavailable");
}
